import { AnimatePresence } from 'motion/react';
import type React from 'react';
import { Suspense } from 'react';
import { getAppEnvClient } from '../lib/app-env/registry';
import type { AppEnvManifestEntry } from '../lib/app-env/types';
import { useIsMobile } from '../lib/hooks/use-is-mobile';
import { useAppSettingsStore } from '../stores/app-settings-store';
import { useWindowStore } from '../stores/window-store';
import type { LifecycleState } from '../types/lifecycle';
import { AppErrorBoundary } from './window/AppErrorBoundary';
import { AppSettingsPanel } from './window/AppSettingsPanel';
import { AppWindowTabs } from './window/AppWindowTabs';
import { BundleLoadingSpinner } from './window/BundleLoadingSpinner';
import { MobileWindowStack } from './window/MobileWindowStack';
import { Window } from './window/Window';

/**
 * Render function type for app components.
 * Consumers must provide this to map appId -> React component.
 */
export interface AppComponentRenderer {
	getComponent: (appId: string) => React.ComponentType<{ windowId: string; meta?: Record<string, unknown> }> | null;
	getManifest: (appId: string) =>
		| {
				label: string;
				showTabs?: boolean;
				fullSizeContent?: boolean;
				storeMeta?: { name: string; version: string; author: string; description: string; permissions?: string[] };
				minRole?: string;
				natsPrefix?: string;
				permission?: string;
				/** Lifecycle state from `installed_apps.lifecycle_state` — drives the
				 *  in-window banner. Optional: when undefined, the banner is suppressed. */
				lifecycleState?: LifecycleState;
				/** Manifest-declared env schema — drives the Settings → Environment tab. */
				env?: AppEnvManifestEntry[];
				/** `installed_apps.id` for this window's app — required for the env client. */
				installedAppId?: string;
		  }
		| undefined;
}

/** Context to provide app rendering capabilities from the consumer. */
let _appRenderer: AppComponentRenderer | null = null;

export function setAppRenderer(renderer: AppComponentRenderer) {
	_appRenderer = renderer;
}

export function getAppRenderer(): AppComponentRenderer | null {
	return _appRenderer;
}

export function WindowRenderer() {
	const windowsByWorkspace = useWindowStore((s) => s.windowsByWorkspace);
	const activeWorkspaceId = useWindowStore((s) => s.activeWorkspaceId);
	const windows = activeWorkspaceId ? windowsByWorkspace[activeWorkspaceId] || [] : [];

	// biome-ignore lint/suspicious/noConsole: debug logging for window renderer
	console.log(`[WindowRenderer] workspace: ${activeWorkspaceId}, windows: ${windows.length}`);

	const isMobile = useIsMobile();
	const renderer = getAppRenderer();
	const settingsRequest = useAppSettingsStore((s) => s.current);
	const closeAppSettings = useAppSettingsStore((s) => s.closeAppSettings);

	if (isMobile) {
		return <MobileWindowStack windows={windows} />;
	}

	return (
		<AnimatePresence>
			{windows.map((win) => {
				const AppComponent = renderer?.getComponent(win.appId);
				if (!AppComponent) return null;

				const manifest = renderer?.getManifest(win.appId);
				const appContent = (
					<Suspense fallback={<BundleLoadingSpinner appName={manifest?.label ?? win.appId} />}>
						<AppComponent windowId={win.id} meta={win.meta} />
					</Suspense>
				);

				const settingsOpen = settingsRequest?.appId === win.appId;
				const envClient = getAppEnvClient() ?? undefined;

				return (
					<Window key={win.id} window={win} lifecycleState={manifest?.lifecycleState}>
						{manifest?.showTabs ? (
							<AppWindowTabs appId={win.appId} manifest={manifest}>
								{appContent}
							</AppWindowTabs>
						) : (
							<AppErrorBoundary appName={manifest?.label ?? win.appId}>{appContent}</AppErrorBoundary>
						)}
						{settingsOpen && (
							<AppSettingsPanel
								key={`settings-${win.id}`}
								windowId={win.id}
								appId={win.appId}
								manifest={
									manifest
										? {
												label: manifest.label,
												permission: manifest.permission,
												minRole: manifest.minRole,
												natsPrefix: manifest.natsPrefix,
												storeMeta: manifest.storeMeta,
												env: manifest.env,
											}
										: undefined
								}
								initialTab={settingsRequest.tab}
								onClose={closeAppSettings}
								envClient={envClient}
								installedAppId={manifest?.installedAppId ?? win.appId}
								isAdmin={false}
							/>
						)}
					</Window>
				);
			})}
		</AnimatePresence>
	);
}
